import {useTranslation} from 'react-i18next';
import {
  Control,
  UseFormWatch,
  UseFormSetValue,
  Controller,
} from 'react-hook-form';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import {FormDataType} from '../../../types';
import {SVG} from '../../../../../../../constants/assetLists';

type Props = {
  index: number;
  control: Control<FormDataType>;
  watch: UseFormWatch<FormDataType>;
  setValue: UseFormSetValue<FormDataType>;
};

function LinearScaleField({index, control, watch, setValue}: Props) {
  const {t} = useTranslation();

  const fromOptions = Array.from({length: 1}, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }));

  const toOptions = Array.from({length: 10}, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }));

  const fromValue = watch(`questions.${index}.fromValue` as any) ?? 1;
  const toValue = watch(`questions.${index}.toValue` as any) ?? 5;

  const emptyFieldText = (field: any) => {
    setValue(field, '', {shouldDirty: true});
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Scale Range */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <span className="text-textColor">
            {t('forms.dialog.linearScale.from')}
          </span>
          <div className="w-[101px]">
            <AppMUIAutocomplete
              size="small"
              options={fromOptions}
              value={
                fromOptions.find(opt => opt.value === fromValue) ||
                fromOptions[0]
              }
              getOptionLabel={option => option.label}
              onChange={(_, newValue) => {
                if (newValue) {
                  setValue(
                    `questions.${index}.fromValue` as any,
                    newValue.value,
                    {
                      shouldDirty: true,
                    },
                  );
                }
              }}
              disableClearable
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="textColor">{t('forms.dialog.linearScale.to')}</span>
          <div className="w-[101px]">
            <AppMUIAutocomplete
              size="small"
              options={toOptions}
              value={
                toOptions.find(opt => opt.value === toValue) || toOptions[4]
              }
              getOptionLabel={option => option.label}
              onChange={(_, newValue) => {
                if (newValue) {
                  setValue(
                    `questions.${index}.toValue` as any,
                    newValue.value,
                    {
                      shouldDirty: true,
                    },
                  );
                }
              }}
              disableClearable
            />
          </div>
        </div>
      </div>

      {/* Labels */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <span className="text-textColor">{fromValue}.</span>

          <div className="flex-1">
            <Controller
              name={`questions.${index}.fromLabel` as any}
              control={control}
              render={({field}) => (
                <AppTextInput
                  {...field}
                  value={field.value || ''}
                  placeholder={t('forms.dialog.linearScale.fromLabel')}
                  className="border-none"
                />
              )}
            />
          </div>

          <button
            type="button"
            onClick={() => emptyFieldText(`questions.${index}.fromLabel`)}>
            <img
              src={SVG.closeDarkIcon}
              alt="remove"
              className="w-[14px] h-[14px] object-contain"
            />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-textColor">{toValue}.</span>

          <div className="flex-1">
            <Controller
              name={`questions.${index}.toLabel` as any}
              control={control}
              render={({field}) => (
                <AppTextInput
                  {...field}
                  value={field.value || ''}
                  placeholder={t('forms.dialog.linearScale.toLabel')}
                  className="border-none"
                />
              )}
            />
          </div>

          <button
            type="button"
            onClick={() => emptyFieldText(`questions.${index}.toLabel`)}>
            <img
              src={SVG.closeDarkIcon}
              alt="remove"
              className="w-[14px] h-[14px] object-contain"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default LinearScaleField;
